import {
    useCallback,
    useEffect,
    useState,
    useMemo,
    type FC,
    useRef
} from "react";
import {
    ScrollView,
    Platform,
    type View
} from "react-native";
import type IDropdownMenuSurfaceProps from "./type";
import type {
    DropdownMenuSurfaceWebPropsType
} from "./type";
import stylesheet, {
    getDropdownMenuSurfaceWidth,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    DropdownMenuLevelContext,
    useDropdownMenuContext,
    DropdownMenuContext
} from "../../context";
import type {
    DropdownMenuKeyboardEventType,
    DropdownMenuRegistryEntryType,
    DropdownMenuLevelContextType
} from "../../type";
import {
    getDropdownMenuTypeaheadIndex,
    DROPDOWN_MENU_FOCUS_OPTIONS,
    getDropdownMenuNextIndex,
    DROPDOWN_MENU_SPACE_KEY,
    getDropdownMenuElement,
    getDropdownMenuItems
} from "../../util";
import {
    getHorizontalStep
} from "../../../../helpers/direction";
import PopoverSurface from "../../../popoverSurface";

const DropdownMenuSurface: FC<IDropdownMenuSurfaceProps> = ({
    customTheme: customThemeProp,
    widthBehaviour = "content",
    triggerWidth = 0,
    onCloseSub,
    anchorRef,
    onDismiss,
    children,
    openMode,
    isOpen,
    offset,
    align,
    level,
    style,
    side,
    id,
    ...props
}) => {
    const rootContext = useDropdownMenuContext({
        part: "Content"
    });

    const customTheme = customThemeProp ?? rootContext?.customTheme;

    const {
        controlHeights,
        durations,
        borders,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        activeLocale,
        direction
    } = NCoreUIKitLocalize.useContext(rootContext?.customLocalize);

    const surfaceRef = useRef<View>(null);

    const registryRef = useRef(new Map<string, DropdownMenuRegistryEntryType>());

    const bufferRef = useRef("");

    const bufferTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [
        activeId,
        setActiveId
    ] = useState<string | null>(null);

    const [
        openSubId,
        setOpenSubId
    ] = useState<string | null>(null);

    const [
        leadingCount,
        setLeadingCount
    ] = useState(0);

    const isWeb = Platform.OS === "web";

    const isSubMenu = level > 0;

    const surfaceWidth = getDropdownMenuSurfaceWidth({
        triggerWidth: isSubMenu ? 0 : triggerWidth,
        widthBehaviour,
        controlHeights
    });

    const {
        content: contentDynamicStyle,
        surface: surfaceDynamicStyle,
        scroll: scrollDynamicStyle
    } = useStyles({
        padding: "spacingXs",
        isSubMenu,
        borders,
        spaces
    });

    const handleLeadingChange = useCallback((delta: number) => {
        setLeadingCount((previousCount) => previousCount + delta);
    }, []);

    const handleActiveChange = useCallback((nextId: string) => {
        setActiveId(nextId);
    }, []);

    const handleSubOpenChange = useCallback((subId: string | null) => {
        setOpenSubId(subId);
    }, []);

    const handleItemHover = useCallback((subId?: string) => {
        if(hoverTimerRef.current !== null) {
            clearTimeout(hoverTimerRef.current);

            hoverTimerRef.current = null;
        }

        if(subId !== undefined) {
            setOpenSubId((previousId) => previousId !== null && previousId !== subId ? null : previousId);

            return;
        }

        hoverTimerRef.current = setTimeout(() => {
            hoverTimerRef.current = null;

            const surfaceElement = getDropdownMenuElement(surfaceRef.current);

            const hoveredMenus = surfaceElement?.ownerDocument.querySelectorAll("[role=\"menu\"]:hover") ?? [];

            if(Array.from(hoveredMenus).some((menu) => menu !== surfaceElement)) {
                return;
            }

            setOpenSubId(null);
        }, durations.hoverGrace);
    }, [
        durations.hoverGrace
    ]);

    const levelValue = useMemo<DropdownMenuLevelContextType>(() => ({
        isReserveLeading: leadingCount > 0,
        handleLeadingChange,
        handleSubOpenChange,
        handleActiveChange,
        handleItemHover,
        registryRef,
        openSubId,
        activeId,
        level
    }), [
        handleSubOpenChange,
        handleLeadingChange,
        handleActiveChange,
        handleItemHover,
        leadingCount,
        openSubId,
        activeId,
        level
    ]);

    useEffect(() => {
        if(isOpen) {
            return;
        }

        setActiveId(null);

        setOpenSubId(null);
    }, [
        isOpen
    ]);

    useEffect(() => {
        return () => {
            if(bufferTimerRef.current !== null) {
                clearTimeout(bufferTimerRef.current);
            }

            if(hoverTimerRef.current !== null) {
                clearTimeout(hoverTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if(!isOpen || !isWeb || (isSubMenu && openMode === "pointer")) {
            return;
        }

        let frame: number | null = null;

        let attempt = 0;

        const moveFocus = () => {
            frame = null;

            const surfaceElement = getDropdownMenuElement(surfaceRef.current);

            const items = surfaceElement ? getDropdownMenuItems({
                surface: surfaceElement
            }) : [];

            if(!surfaceElement || (!items.length && attempt < 10)) {
                attempt += 1;

                frame = requestAnimationFrame(moveFocus);

                return;
            }

            if(openMode === "pointer" || !items.length) {
                surfaceElement.focus(DROPDOWN_MENU_FOCUS_OPTIONS);

                return;
            }

            const target = openMode === "last" ? items[items.length - 1] : items[0];

            target?.focus(DROPDOWN_MENU_FOCUS_OPTIONS);
        };

        moveFocus();

        return () => {
            if(frame !== null) {
                cancelAnimationFrame(frame);
            }
        };
    }, [
        isOpen,
        openMode
    ]);

    const handleTypeahead = ({
        currentIndex,
        items,
        key
    }: {
        items: Array<HTMLElement>;
        currentIndex: number;
        key: string;
    }) => {
        const buffer = `${bufferRef.current}${key.toLocaleLowerCase(activeLocale)}`;

        const index = getDropdownMenuTypeaheadIndex({
            textValues: items.map((item) => registryRef.current.get(item.id)?.textValue ?? ""),
            currentIndex,
            buffer
        });

        bufferRef.current = buffer;

        if(bufferTimerRef.current !== null) {
            clearTimeout(bufferTimerRef.current);
        }

        bufferTimerRef.current = setTimeout(() => {
            bufferRef.current = "";

            bufferTimerRef.current = null;
        }, durations.typeahead);

        items[index]?.focus(DROPDOWN_MENU_FOCUS_OPTIONS);

        return index !== -1;
    };

    const onKeyDown = (event: DropdownMenuKeyboardEventType) => {
        const surfaceElement = getDropdownMenuElement(surfaceRef.current);

        if(!rootContext || !surfaceElement || !(event.target instanceof Node) || !surfaceElement.contains(event.target)) {
            return;
        }

        if(event.key === "Escape") {
            event.preventDefault();

            event.stopPropagation();

            rootContext.handleClose();

            return;
        }

        if(event.key === "Tab") {
            rootContext.handleTabKey();

            return;
        }

        if(event.ctrlKey || event.metaKey || event.altKey) {
            return;
        }

        const items = getDropdownMenuItems({
            surface: surfaceElement
        });

        const currentIndex = items.findIndex((item) => item === surfaceElement.ownerDocument.activeElement);

        const nextIndex = getDropdownMenuNextIndex({
            length: items.length,
            key: event.key,
            currentIndex
        });

        if(nextIndex !== -1) {
            event.preventDefault();

            event.stopPropagation();

            items[nextIndex]?.focus(DROPDOWN_MENU_FOCUS_OPTIONS);

            return;
        }

        const step = getHorizontalStep({
            key: event.key,
            direction
        });

        if(step === 1) {
            const entry = registryRef.current.get(items[currentIndex]?.id ?? "");

            if(entry?.handleOpenSub) {
                event.preventDefault();

                event.stopPropagation();

                entry.handleOpenSub();
            }

            return;
        }

        if(step === -1) {
            if(onCloseSub) {
                event.preventDefault();

                event.stopPropagation();

                onCloseSub();
            }

            return;
        }

        if(!event.key || event.key.length !== 1 || (event.key === DROPDOWN_MENU_SPACE_KEY && !bufferRef.current)) {
            return;
        }

        const isMatched = handleTypeahead({
            key: event.key,
            currentIndex,
            items
        });

        if(isMatched) {
            event.preventDefault();

            event.stopPropagation();
        }
    };

    const webProps: DropdownMenuSurfaceWebPropsType = isWeb ? {
        onKeyDown,
        id
    } : {};

    if(!rootContext) {
        return null;
    }

    return <PopoverSurface
        {...webProps}
        style={[
            surfaceDynamicStyle,
            style
        ]}
        onDismiss={onDismiss}
        isOpen={isOpen}
        {...surfaceWidth}
        customTheme={customTheme}
        anchorRef={anchorRef}
        ref={surfaceRef}
        offset={offset}
        align={align}
        side={side}
        role="menu"
        {...props}
    >
        <DropdownMenuContext.Provider
            value={rootContext}
        >
            <DropdownMenuLevelContext.Provider
                value={levelValue}
            >
                <ScrollView
                    contentContainerStyle={contentDynamicStyle}
                    showsVerticalScrollIndicator={false}
                    style={[
                        stylesheet.scroll,
                        scrollDynamicStyle
                    ]}
                    bounces={false}
                >
                    {children}
                </ScrollView>
            </DropdownMenuLevelContext.Provider>
        </DropdownMenuContext.Provider>
    </PopoverSurface>;
};
export default DropdownMenuSurface;
